import { DatePicker, DatesRangeValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mantine/core';
import { RadioGroup } from 'components/ControlledInputs/RadioGroup';
import { queryParams } from 'utils/query';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { FilterDateChoices } from 'models/filter';

interface Props {
  title: string;
  queryKey: string;
}

export const FilterDates = ({ title, queryKey }: Props) => {
  const { t } = useTranslation();
  const { setParameters, parameters: filters } = useSearchParameters(queryKey);
  const currentChoice = filters.dateChoice || '';

  const computeDateState = (): Date | null => {
    if (filters.startDate) return dayjs(+filters.startDate).toDate();
    if (filters.endDate) return dayjs(+filters.endDate).toDate();
    return null;
  };

  const changeDateParameters = (dates: DatesRangeValue, filter?: string) => {
    setParameters({
      ...filters,
      [queryParams.startDate]: dates[0]?.valueOf().toString() || '',
      [queryParams.endDate]: dates[1]?.valueOf().toString() || '',
      [queryParams.dateChoice]: filter || '',
    });
  };

  const changeDateRangeValues = (dates: DatesRangeValue, filter?: string) => {
    if (!dates[0]) return;

    if (filters.startDate && !filters.endDate) {
      changeDateParameters([dayjs(+filters.startDate).toDate(), new Date(dates[0])], filter);
      return;
    }
    changeDateParameters([new Date(dates[0]), null], filter);
  };

  const changeDateValue = (date: Date | null) => {
    if (filters.dateChoice === FilterDateChoices.beforeDate) {
      changeDateParameters([null, date], FilterDateChoices.beforeDate);
      return;
    }
    if (filters.dateChoice === FilterDateChoices.afterDate) {
      changeDateParameters([date, null], FilterDateChoices.afterDate);
    }
  };

  const handleChoiceChange = (newChoice: string) => {
    const today = dayjs();
    switch (newChoice) {
      case FilterDateChoices.lastWeek:
        changeDateParameters(
          [today.subtract(1, 'week').startOf('day').toDate(), today.endOf('day').toDate()],
          newChoice,
        );
        break;
      case FilterDateChoices.lastMonth:
        changeDateParameters(
          [today.subtract(1, 'month').startOf('day').toDate(), today.endOf('day').toDate()],
          newChoice,
        );
        break;
      case FilterDateChoices.lastThreeMonths:
        changeDateParameters(
          [today.subtract(3, 'months').startOf('day').toDate(), today.endOf('day').toDate()],
          newChoice,
        );
        break;
      case FilterDateChoices.lastYear:
        changeDateParameters(
          [today.subtract(1, 'year').startOf('day').toDate(), today.endOf('day').toDate()],
          newChoice,
        );
        break;
      default:
        changeDateParameters([null, null], newChoice);
        break;
    }
  };

  const renderCalendarInput = () => {
    if (currentChoice.includes('last') || currentChoice === FilterDateChoices.betweenDates) {
      return (
        <DatePicker
          type={'range'}
          value={[dayjs(+filters.startDate).toDate(), dayjs(+filters.endDate).toDate()]}
          onChange={(values) => changeDateRangeValues(values, currentChoice)}
        />
      );
    }
    return (
      <DatePicker
        value={computeDateState()}
        onChange={(value) => value && changeDateValue(new Date(value))}
        style={currentChoice === '' ? { opacity: 0.5, pointerEvents: 'none' } : { opacity: 1, pointerEvents: 'auto' }}
      />
    );
  };

  return (
    <Grid>
      <Grid.Col span={6}>
        <RadioGroup
          data={Object.values(FilterDateChoices).map((value) => ({ value, label: t(`timeframe_${value}`) }))}
          label={title}
          onChange={handleChoiceChange}
          value={currentChoice}
          spacing={'xs'}
          vertical
        />
      </Grid.Col>
      <Grid.Col span={6}>{renderCalendarInput()}</Grid.Col>
    </Grid>
  );
};
